import {
  activateReservationApiCall,
  cancelMultipleReservationsByIdsApiCall,
  cancelReservationApiRequest,
  changeUserReservationPresenceApiCall,
  changeUserReservationsNumberApiCall,
  checkMultipleReservationOverlapApiCall,
  checkReservationOverlapApiCall,
  createMultipleReservationApiCall,
  createReservationApiCall,
  createReservationAsAnonymousApiCall,
  duplicateReservationApiRequest,
  getCustomersSubserviceReservationsApiRequest,
  getOrderByIdApiCall,
  getReservationPresenceApiCall,
  getReservationPriceApiRequest,
  getReservationsForMultipleCancelApiCall,
  updateMultipleReservationsApiRequest,
  updateReservationApiCall,
  updateReservationServiceApiCall,
} from '@guryou/api-client-gql';
import { parseDate } from '@guryou/utils';

import { execute } from '../actions/actionWrapper';
import { resetState } from '../actions/resetActions';
import { Action, basicReducer, handleLocationInput } from '../core';

// Types
export const RESERVATIONS_GET_PRICE = { entity: 'RESERVATIONS_PRICE', action: Action.GET };

export const RESERVATIONS_CREATE = { entity: 'RESERVATIONS', action: Action.CREATE };
export const RESERVATIONS_CREATE_MULTIPLE = { entity: 'RESERVATIONS_CREATE_MULTIPLE', action: Action.CREATE };

export const RESERVATIONS_UPDATE = { entity: 'RESERVATIONS', action: Action.UPDATE };

export const RESERVATIONS_CANCEL = { entity: 'RESERVATIONS', action: Action.ARCHIVE };

export const RESERVATIONS_ACTIVATE = { entity: 'RESERVATIONS_ACTIVATE', action: Action.UPDATE };

export const USER_MEMBERSHIP_RESERVATIONS_NUMBER_UPDATE = { entity: 'USER_MEMBERSHIP_RESERVATIONS_NUMBER', action: Action.UPDATE };

export const RESERVATION_USER_PRESENCE = { entity: 'RESERVATION_USER_PRESENCE', action: Action.UPDATE };

export const USER_RESERVATIONS = { entity: 'USER_RESERVATIONS', action: Action.GET_MANY };

export const GET_RESERVATIONS_FOR_MULTIPLE_CANCEL = { entity: 'GET_RESERVATIONS_FOR_MULTIPLE_CANCEL', action: Action.GET_MANY };

export const RESERVATIONS_MULTIPLE_CANCEL = { entity: 'RESERVATIONS_MULTIPLE_CANCEL', action: Action.UPDATE };

// Modifica N (#1713): data is [{ id, success, error }] per reservation.
export const RESERVATIONS_MULTIPLE_UPDATE = { entity: 'RESERVATIONS_MULTIPLE_UPDATE', action: Action.UPDATE };

// Duplica (#1713): data is [{ index, success, id, error }] per copy.
export const RESERVATIONS_DUPLICATE = { entity: 'RESERVATIONS_DUPLICATE', action: Action.CREATE };

export const RESERVATION_ORDER = { entity: 'RESERVATION_ORDER', action: Action.GET_ONE };

export const UPDATE_RESERVATION_SERVICE = { entity: 'UPDATE_RESERVATION_SERVICE', action: Action.UPDATE };

export const RESERVATION_PRESENCE = { entity: 'RESERVATION_PRESENCE', action: Action.GET_ONE };

export const CHECK_RESERVATION_OVERLAP = { entity: 'CHECK_RESERVATION_OVERLAP', action: Action.GET_ONE };
export const CHECK_MULTIPLE_RESERVATION_OVERLAP = { entity: 'CHECK_MULTIPLE_RESERVATION_OVERLAP', action: Action.GET_ONE };

// Actions
export const createReservation = data => async dispatch => {
  const customerId = +data.customerId || null;
  const customerWalkInId = +data.customerWalkInId || null;
  const email = data.email || (customerId ? null : data.customerId);
  const { dateFrom, timeFrom, orderEvent } = data;

  const request = {
    customerId,
    customerWalkInId,
    email: data.customerSide ? null : email,
    subServiceId: +data.subServiceId,
    resourceId: +data.resourceId,
    isPaid: data.isPaid === 'true',
    price: +data.price ?? null,
    note: data.note,
    dateTimeFrom: data.dateTimeFrom || parseDate(`${dateFrom} ${timeFrom}`).toISOString(),
    useMembership: data.useMembership,
    status: data.status,
    duration: +data.duration || null,
    orderEvent,
    overlapConfirmed: data.overlapConfirmed || null,
    ...handleLocationInput(data),
  };

  if (data?.materialResource) {
    request.materialResource = +data.materialResource;
  }

  return execute(dispatch, () => createReservationApiCall({ request }), RESERVATIONS_CREATE, true);
};

export const createMultipleReservation = data => async dispatch => {
  const customerId = +data.customerId || null;
  const customerWalkInId = +data.customerWalkInId || null;
  const email = data.email || (customerId ? null : data.customerId);
  const workingDays = data.workingDays || null;
  const addedDays = data.addedDays || null;
  const { dateFrom, timeFrom, orderEvent } = data;

  const request = {
    customerId,
    customerWalkInId,
    email: data.customerSide ? null : email,
    subServiceId: +data.subServiceId,
    resourceId: +data.resourceId,
    isPaid: data.isPaid === 'true',
    note: data.note,
    dateTimeFrom: data.dateTimeFrom || parseDate(`${dateFrom} ${timeFrom}`).toISOString(),
    endDate: parseDate(`${data.endDate} ${timeFrom}`).toISOString(),
    useMembership: data.useMembership,
    userMembershipId: data.userMembershipId || null,
    status: data.status,
    duration: +data.duration || null,
    orderEvent,
    overlapConfirmed: data.overlapConfirmed || null,
    repeatInterval: +data.repeatInterval || null,
    repeatIntervalType: data.repeatIntervalType,
    ...handleLocationInput(data),
  };

  if (workingDays) {
    const wdays = Array.isArray(workingDays) ? workingDays : [workingDays];
    request.workingDays = wdays.filter(day => day !== '').map(d => +d);
  }

  if (addedDays) {
    request.addedDays = addedDays;
  }

  if (data?.materialResource) {
    request.materialResource = +data.materialResource;
  }

  return execute(dispatch, () => createMultipleReservationApiCall({ request }), RESERVATIONS_CREATE_MULTIPLE, true);
};

export const createReservationAsAnonymous = data => async dispatch => {
  const { firstname, lastname, phone } = data;
  const request = {
    ...data,
    firstname: firstname || null,
    lastname: lastname || null,
    phone: phone || null,
  };
  execute(dispatch, () => createReservationAsAnonymousApiCall(request), RESERVATIONS_CREATE, true);
};

export const updateReservation = data => async dispatch => {
  const { dateFrom, timeFrom } = data;
  const request = {
    id: +data.id,
    dateTimeFrom: parseDate(`${dateFrom} ${timeFrom}`).toISOString(),
    email: data.email,
    note: data.note,
    resourceId: +data.resourceId,
    subServiceId: +data.subServiceId,
    customerId: +data.customerId || null,
    duration: +data.duration,
    overlapConfirmed: data.overlapConfirmed || null,
    ...handleLocationInput(data),
  };

  execute(dispatch, () => updateReservationApiCall({ request }), RESERVATIONS_UPDATE);
};

// Pre-create check: does this customer already hold an overlapping booking? Drives the
// deal-web staff warning (proceed anyway / cancel) — mirrors checkBundleOverlap.
export const checkReservationOverlap = (subServiceId, resourceId, dateTimeFrom, duration, customerId, email) => async dispatch =>
  execute(
    dispatch,
    () =>
      checkReservationOverlapApiCall(
        {
          subServiceId: +subServiceId,
          resourceId: +resourceId,
          dateTimeFrom,
          duration: +duration || null,
          customerId: +customerId || null,
          email: email || null,
        },
        { bookings: ['id', 'source', 'name', 'startDt', 'endDt'] }
      ),
    CHECK_RESERVATION_OVERLAP,
    false
  );

export const checkMultipleReservationOverlap = (subServiceId, occurrences, customerId, email) => async dispatch => {
  const request = { subServiceId: +subServiceId, occurrences, customerId: +customerId || null, email: email || null };
  return execute(
    dispatch,
    () =>
      checkMultipleReservationOverlapApiCall(request, { conflicts: ['index', 'dateFrom', 'timeFrom', 'bookings { id source name startDt endDt }'] }),
    CHECK_MULTIPLE_RESERVATION_OVERLAP,
    false,
    { signature: JSON.stringify(request) }
  );
};

export const changeUserReservationsNumber = (id, numberOfReservations, startDate, endDate, action) => async dispatch => {
  let startTime = null;
  let endTime = null;
  if (startDate && endDate) {
    startTime = `${startDate}T00:00:00.000Z`;
    endTime = `${endDate}T23:59:59.000Z`;
  }
  execute(
    dispatch,
    () => changeUserReservationsNumberApiCall({ id, numberOfReservations, startTime, endTime, action }),
    USER_MEMBERSHIP_RESERVATIONS_NUMBER_UPDATE
  );
};

export const getCustomersSubserviceReservations = (subserviceId, userId, customerId) => async dispatch =>
  execute(dispatch, () => getCustomersSubserviceReservationsApiRequest(subserviceId, userId, customerId), USER_RESERVATIONS);

export const activateReservation = reservationId => async dispatch =>
  execute(dispatch, () => activateReservationApiCall({ reservationId }), RESERVATIONS_ACTIVATE);

export const cancelReservation = reservationId => async dispatch =>
  execute(dispatch, () => cancelReservationApiRequest(reservationId), RESERVATIONS_CANCEL);

export const getReservationPrice = reservationId => async dispatch =>
  execute(dispatch, () => getReservationPriceApiRequest(reservationId), RESERVATIONS_GET_PRICE);

export const changeUserReservationPresence = (reservationId, presence) => async dispatch => {
  let p = presence;
  if (presence === 'Y') {
    p = 'true';
  }
  if (presence === 'N') {
    p = 'false';
  }
  execute(dispatch, () => changeUserReservationPresenceApiCall({ reservationId, presence: p }), RESERVATION_USER_PRESENCE);
};

export const getReservationPresence = reservationId => async dispatch => {
  execute(dispatch, () => getReservationPresenceApiCall({ reservationId }), RESERVATION_PRESENCE, false);
};

export const getReservationsForMultipleCancel = data => async dispatch => {
  const request = {
    serviceId: +data.serviceId,
    subServiceId: +data.subServiceId || null,
    fromDate: `${data.fromDate}T00:00:00`,
    toDate: `${data.toDate}T23:59:00`,
    userId: +data.userId || null,
    customerId: +data.customerId || null,
    resourceId: +data.resourcesId || null,
  };
  execute(dispatch, () => getReservationsForMultipleCancelApiCall({ request }), GET_RESERVATIONS_FOR_MULTIPLE_CANCEL);
};
export const cancelMultipleReservations = reservationIds => async dispatch => {
  execute(dispatch, () => cancelMultipleReservationsByIdsApiCall({ reservationIds }), RESERVATIONS_MULTIPLE_CANCEL);
};

// Modifica N (#1713): each request is updateReservation's input; saved one by one by the API.
export const updateMultipleReservations = requests => async dispatch => {
  execute(dispatch, () => updateMultipleReservationsApiRequest({ requests }), RESERVATIONS_MULTIPLE_UPDATE);
};

// Duplica (#1713): copies of a reservation, created one by one by the API as new bookings.
export const duplicateReservation = (reservationId, copies) => async dispatch => {
  execute(dispatch, () => duplicateReservationApiRequest({ reservationId, copies }), RESERVATIONS_DUPLICATE);
};

export const getReservationOrder = orderId => async dispatch => {
  execute(
    dispatch,
    () => getOrderByIdApiCall({ orderId }, { events: ['id', 'price'], userMembership: ['id', 'name'] }),
    RESERVATION_ORDER,
    false,
    null,
    true
  );
};

export const updateReservationService = data => async dispatch => {
  const request = {
    orderId: +data.orderId,
    oldResourceId: +data.oldResourceId,
    oldMaterialResourceId: +data.oldMaterialResourceId || null,
    oldUserMembershipId: +data.oldUserMembershipId || null,
    newServiceId: +data.newServiceId,
    newSubserviceId: +data.newSubserviceId,
    newResourceId: +data.newResourceId,
    newMaterialResourceId: +data.newMaterialResourceId || null,
    newUserMembershipId: +data.newUserMembershipId || null,
  };
  execute(dispatch, () => updateReservationServiceApiCall({ request }), UPDATE_RESERVATION_SERVICE, true);
};

// Reset actions
export const resetCreateReservationState = () => async dispatch => dispatch(resetState(RESERVATIONS_CREATE));

// Reducers
export const reservationReducers = {
  create: basicReducer(RESERVATIONS_CREATE),
  createMultiple: basicReducer(RESERVATIONS_CREATE_MULTIPLE),
  update: basicReducer(RESERVATIONS_UPDATE),
  cancel: basicReducer(RESERVATIONS_CANCEL),
  activate: basicReducer(RESERVATIONS_ACTIVATE),
  userMembershipReservations: basicReducer(USER_MEMBERSHIP_RESERVATIONS_NUMBER_UPDATE),
  userPresence: basicReducer(RESERVATION_USER_PRESENCE),
  userReservations: basicReducer(USER_RESERVATIONS),
  reservationsForCancel: basicReducer(GET_RESERVATIONS_FOR_MULTIPLE_CANCEL),
  reservationsMultiCancel: basicReducer(RESERVATIONS_MULTIPLE_CANCEL),
  reservationsMultiUpdate: basicReducer(RESERVATIONS_MULTIPLE_UPDATE),
  duplicate: basicReducer(RESERVATIONS_DUPLICATE),
  reservationOrder: basicReducer(RESERVATION_ORDER),
  updateReservationService: basicReducer(UPDATE_RESERVATION_SERVICE),
  reservationPresence: basicReducer(RESERVATION_PRESENCE),
  reservationOverlap: basicReducer(CHECK_RESERVATION_OVERLAP),
  multipleReservationOverlap: basicReducer(CHECK_MULTIPLE_RESERVATION_OVERLAP),
};

export const reservationFeelReducers = {
  create: basicReducer(RESERVATIONS_CREATE),
  cancel: basicReducer(RESERVATIONS_CANCEL),
};
